// @flow
import { Button, Checkbox, CircularProgress, Container, CssBaseline, FormControlLabel, Grid, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import routes from '../routes';
import Layout from '../component/Layout';
import { AuthContext } from '../context/auth';
import User from '../model/User';
import { isNotValidEmail, isNotValidName, isNotValidPassword, isNotValidPhone } from '../util/validator';
import ApiResponse from '../model/ApiResponse';

const privacyPolicyUrl: string = String(process.env.REACT_APP_PRIVACY_POLICY_URL);
const marketingCampaignUrl: string = String(process.env.REACT_APP_MARKETING_CAMPAIGN_URL);

const useStyles = makeStyles((theme) => ({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  error: {
    color: theme.palette.error.main,
  },
}));

const Profile = () => {
  const { t } = useTranslation();
  const { user, doPost, setUser } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const validate = values => {
    const errors = {};
    if (!values.name) {
      errors.name = t('Required');
    } else if (isNotValidName(values.name)) {
      errors.name = t('Invalid name');
    }
    if (!values.email) {
      errors.email = t('Required');
    } else if (isNotValidEmail(values.email)) {
      errors.email = t('Invalid email address');
    }
    if (!values.phone) {
      errors.phone = t('Required');
    } else if (isNotValidPhone(values.phone)) {
      errors.phone = t('Invalid phone');
    }
    if (values.password && isNotValidPassword(values.password)) {
      errors.password = t('Invalid password');
    }
    if (!!user && !user.hasTerms && !values.policy) {
      errors.policy = t('Required');
    }
    return errors;
  };

  const onSubmit = async (values, { setSubmitting }) => {
    const data = {
      ...values,
      privacyPolicy: values.policy,
      marketingCampaign: values.marketing,
      deviceData: navigator.userAgent,
    };
    const response: ApiResponse = await doPost('/customer/me', data);
    if (response.ok && response.data) {
      const user = User.parse(response.data);
      if (user) {
        await setUser(user);
      }
      enqueueSnackbar(t('Data saved'), {
        variant: 'success',
      });
    } else if (!response.is401Error()) {
      enqueueSnackbar(t(response.message), {
        variant: 'error',
      });
      setSubmitting(false);
    }
  };

  return (
    <Layout activeKey={routes.profile}>
      <Container component="main" maxWidth="sm">
        <CssBaseline />
        <div className={classes.paper}>
          {!!user && !user.hasTerms && <Grid item xs={12}>
            <h4 className={classes.error}>{t('Accept mandatory terms')}</h4>
          </Grid>}
          {!!user && user.isRetail && <Grid item xs={12}>
            {t('Your code')}: <b>{user.code}</b>
          </Grid>}
          <Formik
            initialValues={{
              name: user ? user.name : '',
              email: user ? user.email : '',
              phone: user ? user.phone : '',
              password: '',
              policy: user && user.privacyPolicy,
              marketing: user && user.marketingCampaign,
              deviceData: 'TODO',
            }}
            validate={validate}
            onSubmit={onSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => (
              <form className={classes.form} onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      disabled={isSubmitting}
                      autoComplete="name"
                      name="name"
                      variant="outlined"
                      required
                      fullWidth
                      id="name"
                      label={t('Name')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.name}
                    />
                    <span className={classes.error}>{errors.name && touched.name && errors.name}</span>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      disabled={isSubmitting}
                      variant="outlined"
                      required
                      fullWidth
                      id="email"
                      label={t('Email')}
                      name="email"
                      autoComplete="email"
                      type="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                    />
                    <span className={classes.error}>{errors.email && touched.email && errors.email}</span>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      disabled={isSubmitting}
                      variant="outlined"
                      required
                      fullWidth
                      id="phone"
                      label={t('Phone')}
                      name="phone"
                      autoComplete="phone"
                      type="tel"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.phone}
                    />
                    <span className={classes.error}>{errors.phone && touched.phone && errors.phone}</span>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      disabled={isSubmitting}
                      variant="outlined"
                      fullWidth
                      name="password"
                      label={t('Password')}
                      type="password"
                      id="password"
                      autoComplete="current-password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password}
                    />
                    <span className={classes.error}>{errors.password && touched.password && errors.password}</span>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    <FormControlLabel
                      control={<Checkbox
                        disabled={isSubmitting}
                        checked={values.policy}
                        onChange={(e) => setFieldValue('policy', e.target.checked)}
                        name='policy'
                        color='primary' />}
                      label={<a href={privacyPolicyUrl} target='_blank' rel='noopener noreferrer'>
                        <small>{t('Accept privacy policy')}</small>
                      </a>} />
                    <span className={classes.error}>{errors.policy && touched.policy && errors.policy}</span>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    <FormControlLabel
                      control={<Checkbox
                        disabled={isSubmitting}
                        checked={values.marketing}
                        onChange={(e) => setFieldValue('marketing', e.target.checked)}
                        name='marketing'
                        color='primary' />}
                      label={<a href={marketingCampaignUrl} target='_blank' rel='noopener noreferrer'>
                        <small>{t('Accept marketing campaigns')}</small>
                      </a>} />
                    <span className={classes.error}>{errors.marketing && touched.marketing && errors.marketing}</span>
                  </Grid>
                </Grid>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  type="submit"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress color="secondary" size="1.2em" /> : null}
                >
                  {t('Save')}
                </Button>
              </form>
            )}
          </Formik>
        </div>
      </Container>
    </Layout>
  );
};

export default Profile;
